import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../services/supabaseClient';
import { getDashboardData, archiveBookById, restoreBookById, deleteBookById } from '../../services/compositionApi';
import BookCardLuxe from './BookCardLuxe';
import Loading from '../common/Loading';
import {
  IconBook,
  IconArchive,
  IconPlus
} from './DashboardIcons';
import {
  getBookLifecycleStatusFromBook,
  isBookLifecycleAtLeast
} from '../../utils/bookLifecycle';
import '../../styles/luxe-theme.css';
import './DashboardLuxe.css';

const DashboardGeneralLuxe = () => {
  const { t, i18n } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [books, setBooks] = useState([]);
  const [archivedBooks, setArchivedBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filtres pilule (maquette "Maquettes Bookipix", 2026-10-11) : remplacent
  // le bouton "Afficher les archives" a part — un seul jeu d'onglets sur
  // UNE liste combinee, plutot que deux sections toujours separees. `books`
  // et `archivedBooks` restent deux etats distincts (c'est sur eux que
  // portent l'archivage/la restauration/la suppression, inchanges
  // ci-dessous) ; seul l'AFFICHAGE les recombine, voir visibleEntries.
  const [filter, setFilter] = useState('all');
  const [pageNotice, setPageNotice] = useState(null);

  const [stats, setStats] = useState({
    enCours: { count: 0, photos: 0, contributions: 0 },
    termines: { count: 0, photos: 0, contributions: 0 },
    archives: { count: 0, photos: 0, contributions: 0 }
  });

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    bookId: null,
    bookTitle: '',
    deleting: false
  });

  const [archiveModal, setArchiveModal] = useState({
    show: false,
    bookId: null,
    bookTitle: '',
    duration: 3,
    archiving: false
  });

  useEffect(() => {
    checkUser();
  }, []);

  const getBookPhotosCount = (book) => (book?.content_items || []).filter((item) => item.kind === 'photo').length;
  const getBookContributionsCount = (book) => new Set(
    (book?.content_items || []).map((item) => item.contribution_id).filter(Boolean)
  ).size;

  const isFinalizedBook = (book) => (
    isBookLifecycleAtLeast(getBookLifecycleStatusFromBook(book), 'finalized')
  );

  const showNotice = (message, type = 'info') => {
    setPageNotice({ message, type });
  };

  const dismissNotice = () => {
    setPageNotice(null);
  };

  const checkUser = async () => {
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        navigate('/login');
        return;
      }
      setUser(authUser);
      loadUserBooks(authUser.id);
    } catch (error) {
      console.error('Erreur utilisateur dashboard:', error);
      navigate('/login');
    }
  };

  const loadUserBooks = async (userId) => {
    try {
      setLoading(true);

      // Livres actifs, archives (avec leurs elements et participants) et commandes : UNE requete a l'API, qui rend les
      // memes donnees que les trois lectures directes de la base d'avant (chantier « hors Supabase », 2026-10-18).
      const dashboard = await getDashboardData();
      const activeBooksResult = { data: dashboard.activeBooks };
      const archivedBooksResult = { data: dashboard.archivedBooks };
      const ordersResult = { data: dashboard.orders };

      const latestOrderByBook = new Map();
      (ordersResult.data || []).forEach((order) => {
        const bookKey = String(order?.book_id || '').trim();
        if (!bookKey || latestOrderByBook.has(bookKey)) {
          return;
        }
        latestOrderByBook.set(bookKey, order);
      });

      const attachLatestOrder = (bookList) => (
        (bookList || []).map((book) => ({
          ...book,
          latestOrder: latestOrderByBook.get(book.id) || null
        }))
      );

      const activeBooks = attachLatestOrder(activeBooksResult.data);
      const archivedBooksList = attachLatestOrder(archivedBooksResult.data);
      setBooks(activeBooks);
      setArchivedBooks(archivedBooksList);

      const enCoursLivres = activeBooks.filter((book) => !isFinalizedBook(book));
      const terminesLivres = activeBooks.filter((book) => isFinalizedBook(book));

      const aggregate = (bookList) => {
        let photos = 0;
        let contributions = 0;

        bookList.forEach((book) => {
          photos += getBookPhotosCount(book);
          contributions += getBookContributionsCount(book);
        });

        return { photos, contributions };
      };

      const enCoursAgg = aggregate(enCoursLivres);
      const terminesAgg = aggregate(terminesLivres);
      const archivesAgg = aggregate(archivedBooksList);

      setStats({
        enCours: {
          count: enCoursLivres.length,
          photos: enCoursAgg.photos,
          contributions: enCoursAgg.contributions
        },
        termines: {
          count: terminesLivres.length,
          photos: terminesAgg.photos,
          contributions: terminesAgg.contributions
        },
        archives: {
          count: archivedBooksList.length,
          photos: archivesAgg.photos,
          contributions: archivesAgg.contributions
        }
      });
    } catch (error) {
      console.error('Erreur chargement livres:', error);
      showNotice(t('general.loadBooksFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openArchiveModal = (bookId, bookTitle) => {
    setArchiveModal({
      show: true,
      bookId,
      bookTitle,
      duration: 3,
      archiving: false
    });
  };

  const closeArchiveModal = () => {
    setArchiveModal({
      show: false,
      bookId: null,
      bookTitle: '',
      duration: 3,
      archiving: false
    });
  };

  const handleArchiveBook = async () => {
    if (!archiveModal.bookId) return;
    setArchiveModal((prev) => ({ ...prev, archiving: true }));

    try {
      const bookId = archiveModal.bookId;
      const months = archiveModal.duration;
      const autoDeleteDate = new Date();
      autoDeleteDate.setMonth(autoDeleteDate.getMonth() + months);

      const { error } = await archiveBookById(bookId, months);

      if (error) throw error;

      const archivedBook = books.find((book) => book.id === bookId);
      if (!archivedBook) {
        closeArchiveModal();
        await loadUserBooks(user.id);
        return;
      }

      const photosCount = getBookPhotosCount(archivedBook);
      const contributionsCount = getBookContributionsCount(archivedBook);

      setStats((prev) => {
        const newStats = {
          enCours: { ...prev.enCours },
          termines: { ...prev.termines },
          archives: { ...prev.archives }
        };

        if (isFinalizedBook(archivedBook)) {
          newStats.termines.count -= 1;
          newStats.termines.photos -= photosCount;
          newStats.termines.contributions -= contributionsCount;
        } else {
          newStats.enCours.count -= 1;
          newStats.enCours.photos -= photosCount;
          newStats.enCours.contributions -= contributionsCount;
        }

        newStats.archives.count += 1;
        newStats.archives.photos += photosCount;
        newStats.archives.contributions += contributionsCount;

        return newStats;
      });
      setBooks((prev) => prev.filter((book) => book.id !== bookId));
      setArchivedBooks((prev) => [{ ...archivedBook, status: 'archive' }, ...prev]);

      closeArchiveModal();
      showNotice(
        t('general.archivedFor', { months, date: autoDeleteDate.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'fr-FR') }),
        'success'
      );
    } catch (error) {
      console.error('Erreur archivage:', error);
      showNotice(t('general.archiveFailed', { message: error.message }), 'error');
      setArchiveModal((prev) => ({ ...prev, archiving: false }));
    }
  };

  const handleRestoreBook = async (bookId) => {
    try {
      const { error } = await restoreBookById(bookId);

      if (error) throw error;

      const restoredBook = archivedBooks.find((book) => book.id === bookId);
      if (!restoredBook) {
        await loadUserBooks(user.id);
        return;
      }

      const photosCount = getBookPhotosCount(restoredBook);
      const contributionsCount = getBookContributionsCount(restoredBook);

      setStats((prev) => {
        const newStats = {
          enCours: { ...prev.enCours },
          termines: { ...prev.termines },
          archives: { ...prev.archives }
        };

        newStats.archives.count -= 1;
        newStats.archives.photos -= photosCount;
        newStats.archives.contributions -= contributionsCount;

        if (isFinalizedBook(restoredBook)) {
          newStats.termines.count += 1;
          newStats.termines.photos += photosCount;
          newStats.termines.contributions += contributionsCount;
        } else {
          newStats.enCours.count += 1;
          newStats.enCours.photos += photosCount;
          newStats.enCours.contributions += contributionsCount;
        }

        return newStats;
      });
      setArchivedBooks((prev) => prev.filter((book) => book.id !== bookId));
      setBooks((prev) => [{ ...restoredBook, status: 'actif' }, ...prev]);

      showNotice(t('general.restoredSuccess'), 'success');
    } catch (error) {
      console.error('Erreur restauration:', error);
      showNotice(t('general.restoreFailed', { message: error.message }), 'error');
    }
  };

  const openDeleteModal = (bookId, bookTitle) => {
    setDeleteModal({
      show: true,
      bookId,
      bookTitle,
      deleting: false
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      show: false,
      bookId: null,
      bookTitle: '',
      deleting: false
    });
  };

  const handleDeleteBook = async () => {
    if (!deleteModal.bookId) return;
    setDeleteModal((prev) => ({ ...prev, deleting: true }));

    try {
      const bookId = deleteModal.bookId;

      // book_content_items et book_pages sont en "on delete cascade" sur
      // books.id (voir phase03_data_model.sql) : rien a nettoyer manuellement
      // pour ces deux tables. book_contributors, lui, reste un concept actif
      // (acces/droits sur le livre), donc toujours nettoye explicitement.
      // (Le nettoyage de book_contributors est fait par le serveur dans la meme requete.)
      const { error: bookError } = await deleteBookById(bookId);

      if (bookError) throw bookError;

      const isActive = books.some((book) => book.id === bookId);

      if (isActive) {
        const deletedBook = books.find((book) => book.id === bookId);
        const photosCount = getBookPhotosCount(deletedBook);
        const contributionsCount = getBookContributionsCount(deletedBook);
        setBooks((prev) => prev.filter((book) => book.id !== bookId));
        setStats((prev) => {
          const newStats = {
            enCours: { ...prev.enCours },
            termines: { ...prev.termines },
            archives: { ...prev.archives }
          };

          if (isFinalizedBook(deletedBook)) {
            newStats.termines.count -= 1;
            newStats.termines.photos -= photosCount;
            newStats.termines.contributions -= contributionsCount;
          } else {
            newStats.enCours.count -= 1;
            newStats.enCours.photos -= photosCount;
            newStats.enCours.contributions -= contributionsCount;
          }

          return newStats;
        });
      } else {
        const deletedBook = archivedBooks.find((book) => book.id === bookId);
        const photosCount = getBookPhotosCount(deletedBook);
        const contributionsCount = getBookContributionsCount(deletedBook);
        setArchivedBooks((prev) => prev.filter((book) => book.id !== bookId));
        setStats((prev) => ({
          ...prev,
          archives: {
            ...prev.archives,
            count: prev.archives.count - 1,
            photos: prev.archives.photos - photosCount,
            contributions: prev.archives.contributions - contributionsCount
          }
        }));
      }

      closeDeleteModal();
      showNotice(t('general.deletedSuccess'), 'success');
    } catch (error) {
      console.error('Erreur suppression:', error);
      showNotice(t('general.deleteFailed', { message: error.message }), 'error');
      setDeleteModal((prev) => ({ ...prev, deleting: false }));
    }
  };

  if (loading) return <Loading message={t('general.loading')} />;

  // Liste combinee pour l'AFFICHAGE seulement : books/archivedBooks restent
  // les etats sources, inchanges, que les actions archiver/restaurer/
  // supprimer continuent de manipuler directement ci-dessus.
  const entries = [
    ...books.map((book) => ({ book, group: isFinalizedBook(book) ? 'fini' : 'cours', isArchived: false })),
    ...archivedBooks.map((book) => ({ book, group: 'arch', isArchived: true }))
  ];
  const visibleEntries = filter === 'all' ? entries : entries.filter((entry) => entry.group === filter);
  const totalCount = stats.enCours.count + stats.termines.count + stats.archives.count;

  const FILTERS = [
    { key: 'all', label: t('general.filterAll'), count: totalCount },
    { key: 'cours', label: t('general.filterInProgress'), count: stats.enCours.count },
    { key: 'fini', label: t('general.filterFinished'), count: stats.termines.count },
    { key: 'arch', label: t('general.filterArchived'), count: stats.archives.count }
  ];

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <div className="dashboard-toolbar">
          <div className="dashboard-header">
            <div className="dashboard-eyebrow">{t('general.eyebrow')}</div>
            <h1>{t('general.greeting', { name: user?.user_metadata?.full_name || user?.email })}</h1>
            <p>{t('general.subtitle')}</p>
          </div>

          <div className="quick-actions dashboard-header-actions">
            <Link to="/create-book" className="btn-new">
              <IconPlus />
              {t('general.newBook')}
            </Link>
          </div>
        </div>

        {pageNotice?.message && (
          <div className={`dashboard-feedback-banner is-${pageNotice.type || 'info'}`}>
            <span>{pageNotice.message}</span>
            <button
              type="button"
              className="dashboard-feedback-close"
              onClick={dismissNotice}
              aria-label={t('general.closeNotice')}
            >
              x
            </button>
          </div>
        )}

        {/* Filtres pilule (maquette "Maquettes Bookipix", 2026-10-11) :
            remplacent la ligne de chiffres + le bouton d'archives a part. */}
        <div className="dashboard-filters" role="group" aria-label={t('general.myBooks')}>
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label} <b>{f.count}</b>
            </button>
          ))}
        </div>

        <div className="dashboard-section-panel">
          {totalCount === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><IconBook /></div>
              <h3>{t('general.noBooksYet')}</h3>
              <p>{t('general.createFirstBook')}</p>
              <Link to="/create-book" className="btn-new">
                <IconPlus />
                {t('general.createBook')}
              </Link>
            </div>
          ) : filter === 'arch' && visibleEntries.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon"><IconArchive /></div>
              <h3>{t('general.noArchivedBooks')}</h3>
              <p>{t('general.archivedBooksAppearHere')}</p>
            </div>
          ) : (
            <div className="books-grid">
              {visibleEntries.map(({ book, isArchived }) => (
                <BookCardLuxe
                  key={book.id}
                  book={book}
                  onArchive={() => openArchiveModal(book.id, book.title)}
                  onRestore={() => handleRestoreBook(book.id)}
                  onDelete={() => openDeleteModal(book.id, book.title)}
                  showArchive={!isArchived}
                  showRestore={isArchived}
                  autoDeleteDate={isArchived ? book.auto_delete_at : undefined}
                />
              ))}
              {/* Carte "Nouveau livre" toujours presente dans la liste, en
                  plus du bouton du haut (maquette, page Tableau de bord) —
                  sauf dans l'onglet Archives, ou elle n'a pas de sens. */}
              {filter !== 'arch' && (
                <Link to="/create-book" className="dashboard-book-card-new">
                  <span className="dashboard-book-card-new-plus"><IconPlus /></span>
                  {t('general.newBook')}
                </Link>
              )}
            </div>
          )}
        </div>
      </div>

      {archiveModal.show && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title">{t('general.archiveModal.title')}</h3>
            <p className="modal-text">
              {t('general.archiveModal.confirm', { title: archiveModal.bookTitle })}
            </p>
            <select
              value={archiveModal.duration}
              onChange={(event) => setArchiveModal({ ...archiveModal, duration: parseInt(event.target.value, 10) })}
              className="modal-select"
            >
              <option value={3}>{t('general.archiveModal.duration3')}</option>
              <option value={6}>{t('general.archiveModal.duration6')}</option>
              <option value={12}>{t('general.archiveModal.duration12')}</option>
            </select>
            <div className="modal-actions">
              <button
                onClick={closeArchiveModal}
                className="modal-btn modal-btn-cancel"
                disabled={archiveModal.archiving}
              >
                {t('general.archiveModal.cancel')}
              </button>
              <button
                onClick={handleArchiveBook}
                className="modal-btn modal-btn-archive"
                disabled={archiveModal.archiving}
              >
                {archiveModal.archiving ? t('general.archiveModal.archiving') : t('general.archiveModal.archive')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteModal.show && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h3 className="modal-title modal-title-danger">{t('general.deleteModal.title')}</h3>
            <p className="modal-text">
              {t('general.deleteModal.confirm', { title: deleteModal.bookTitle })}
            </p>
            <div className="modal-actions">
              <button
                onClick={closeDeleteModal}
                className="modal-btn modal-btn-cancel"
                disabled={deleteModal.deleting}
              >
                {t('general.deleteModal.cancel')}
              </button>
              <button
                onClick={handleDeleteBook}
                className="modal-btn modal-btn-delete"
                disabled={deleteModal.deleting}
              >
                {deleteModal.deleting ? t('general.deleteModal.deleting') : t('general.deleteModal.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardGeneralLuxe;
